import { Analytics } from '@easy-expense/analytics-client';
import type { FreeLimitFeature } from '@easy-expense/analytics-event-schema';
import { currentUser } from '@easy-expense/auth-client';
import { useCurrentWorkspace } from '@easy-expense/data-firestore-client';
import Data from '@easy-expense/frontend-data-layer';
import {
  decideMeteredAction,
  decidePlanGate,
  RequiredTier,
  wasCreatedThisMonth,
} from '@easy-expense/utils-shared';
import React from 'react';

import { PlanGateContext } from '../components/Paywall/PlanGate.context';
import { useDisablePaywall } from '../lib/disablePaywall';

export enum MeteredPaywallActions {
  SCAN_RECEIPT = 'SCAN_RECEIPT',
  CREATE_REPORT = 'CREATE_REPORT',
  REVIEW_BANK_TRANSACTION = 'REVIEW_BANK_TRANSACTION',
  REVIEW_EMAIL_TRANSACTION = 'REVIEW_EMAIL_TRANSACTION',
  MULTI_CURRENCY = 'MULTI_CURRENCY',
  LINK_MULTIPLE_ACCOUNTS = 'LINK_MULTIPLE_ACCOUNTS',
  RECONCILE_STATEMENTS = 'RECONCILE_STATEMENTS',
  CREATE_WORKSPACE = 'CREATE_WORKSPACE',
  INVITE_MEMBER = 'INVITE_MEMBER',
}

type MeteredActionConfig = {
  /** Which plan lifts the gate. */
  requiredTier: RequiredTier;
  /** Whether the action also needs a free seat on that plan. */
  seatLimited: boolean;
  /** `paywall_name` and `source` reported on `paywall_shown`. */
  paywallName: string;
  paywallSource: string;
  /** Items a free user may create per calendar month; absent for a hard gate. */
  limit?: number;
  /** This month's count in the current workspace. Called as a hook, once per render. */
  useCountThisMonth?: () => number;
  /** User property written on every allowed free use of a metered action. */
  identifyKey?: string;
  /** `feature` reported on `free_limit_usage`; absent for actions that never reported it. */
  usageFeature?: FreeLimitFeature;
};

const useScannedReceiptsThisMonth = () => {
  const expenses = Data.expenses.use();
  return React.useMemo(
    () =>
      expenses.filter(
        (expense) => expense.sourceType === 'scan' && wasCreatedThisMonth(expense.createdAt),
      ).length,
    [expenses],
  );
};

const useReportsCreatedThisMonth = () => {
  const reports = Data.reports.use();
  return React.useMemo(
    () => reports.filter((report) => wasCreatedThisMonth(report.createdAt)).length,
    [reports],
  );
};

const useTransactionsReviewedThisMonth = () => {
  const expenses = Data.expenses.use();
  const incomes = Data.incomes.use();
  return React.useMemo(() => {
    const reviewedExpenses = expenses.filter(
      (expense) =>
        (expense.sourceType === 'email' || expense.sourceType === 'bank') &&
        wasCreatedThisMonth(expense.createdAt),
    ).length;
    const reviewedIncomes = incomes.filter(
      (income) => income.sourceType === 'bank' && wasCreatedThisMonth(income.createdAt),
    ).length;
    return reviewedExpenses + reviewedIncomes;
  }, [expenses, incomes]);
};

const PLAN_GATE = 'web_plan_gate';

const ACTIONS: Record<MeteredPaywallActions, MeteredActionConfig> = {
  [MeteredPaywallActions.SCAN_RECEIPT]: {
    requiredTier: 'any',
    seatLimited: false,
    paywallName: 'metered',
    paywallSource: 'receipt_scans',
    limit: 10,
    useCountThisMonth: useScannedReceiptsThisMonth,
    identifyKey: 'numScannedReceiptsThisMonth',
    usageFeature: 'receipts',
  },
  [MeteredPaywallActions.CREATE_REPORT]: {
    requiredTier: 'any',
    seatLimited: false,
    paywallName: 'metered',
    paywallSource: 'reports',
    limit: 2,
    useCountThisMonth: useReportsCreatedThisMonth,
    identifyKey: 'numReportsCreatedThisMonth',
  },
  // Bank and email reviews share one allowance and one counter; only the wall's copy differs.
  [MeteredPaywallActions.REVIEW_BANK_TRANSACTION]: {
    requiredTier: 'any',
    seatLimited: false,
    paywallName: 'metered',
    paywallSource: 'transaction_reviews',
    limit: 10,
    useCountThisMonth: useTransactionsReviewedThisMonth,
    identifyKey: 'numTransactionsReviewedThisMonth',
    usageFeature: 'transactions',
  },
  [MeteredPaywallActions.REVIEW_EMAIL_TRANSACTION]: {
    requiredTier: 'any',
    seatLimited: false,
    paywallName: 'metered',
    paywallSource: 'transaction_reviews',
    limit: 10,
    useCountThisMonth: useTransactionsReviewedThisMonth,
    identifyKey: 'numTransactionsReviewedThisMonth',
    usageFeature: 'transactions',
  },
  [MeteredPaywallActions.MULTI_CURRENCY]: {
    requiredTier: 'any',
    seatLimited: false,
    paywallName: PLAN_GATE,
    paywallSource: 'multi_currency',
  },
  [MeteredPaywallActions.LINK_MULTIPLE_ACCOUNTS]: {
    requiredTier: 'pro',
    seatLimited: false,
    paywallName: PLAN_GATE,
    paywallSource: 'link_multiple_accounts',
  },
  [MeteredPaywallActions.RECONCILE_STATEMENTS]: {
    requiredTier: 'pro',
    seatLimited: false,
    paywallName: PLAN_GATE,
    paywallSource: 'reconciliation',
  },
  [MeteredPaywallActions.CREATE_WORKSPACE]: {
    requiredTier: 'pro',
    seatLimited: false,
    paywallName: PLAN_GATE,
    paywallSource: 'create_workspace',
  },
  [MeteredPaywallActions.INVITE_MEMBER]: {
    requiredTier: 'pro',
    seatLimited: true,
    paywallName: PLAN_GATE,
    paywallSource: 'invite_member',
  },
};

const usePlanGate = () => {
  const context = React.useContext(PlanGateContext);
  if (!context) {
    throw new Error('useHandleMeteredAction must be used inside PlanGateProvider');
  }
  return context;
};

/**
 * Gates a plan-limited web action, the way ee-mobile and ei-mobile gate theirs.
 *
 * Returns a function that runs the action, or shows the shared upgrade modal when the
 * workspace's plan does not cover it. Metered actions also count this month's items against
 * the free allowance, skip workspaces created before the metered-paywall cutoff, and report
 * usage like the mobile gates. A blocked action is not replayed after a purchase; the user
 * tries again. `onBeforePaywall` runs just before the modal opens, `onPaywallDismissed` when
 * it closes. `action` must not change for the life of the calling component, because each
 * metered action counts through its own hook. With staging's Disable Paywall on, every action
 * runs straight through: no wall, no usage reporting.
 *
 * @example
 * const handleMeteredCreateReport = useHandleMeteredAction(MeteredPaywallActions.CREATE_REPORT);
 * handleMeteredCreateReport(() => createReport());
 */
export const useHandleMeteredAction = (
  action: MeteredPaywallActions,
  options?: { onPaywallDismissed?: () => void },
) => {
  const config = ACTIONS[action];
  const { hasSubscription, hasProPlanSubscription, seats, showPaywall } = usePlanGate();
  const workspace = useCurrentWorkspace();
  const countThisMonth = config.useCountThisMonth?.() ?? 0;
  const disablePaywall = useDisablePaywall();

  const openPaywall = (decision: 'upgrade' | 'add-seats', onBeforePaywall?: () => void) => {
    Analytics.track('paywall_shown', {
      paywall_name: config.paywallName,
      source: config.paywallSource,
    });
    onBeforePaywall?.();
    const usage =
      config.limit === undefined ? undefined : { count: countThisMonth, limit: config.limit };
    showPaywall({ action, decision, usage, onDismiss: options?.onPaywallDismissed });
  };

  const reportFreeUsage = () => {
    Analytics.identify(currentUser()?.uid ?? null, { [config.identifyKey ?? '']: countThisMonth });
    if (config.usageFeature && config.limit !== undefined) {
      Analytics.track('free_limit_usage', {
        feature: config.usageFeature,
        count: countThisMonth,
        limit: config.limit,
      });
    }
  };

  return async (onHandleAction: () => void | Promise<void>, onBeforePaywall?: () => void) => {
    if (disablePaywall) {
      await onHandleAction();
      return;
    }

    const hasRequiredSubscription =
      config.requiredTier === 'pro' ? hasProPlanSubscription : hasSubscription;

    if (config.limit !== undefined) {
      const metered = decideMeteredAction({
        hasRequiredSubscription,
        countThisMonth,
        limit: config.limit,
        workspace,
      });
      if (metered === 'paywall') {
        openPaywall('upgrade', onBeforePaywall);
        return;
      }
      if (metered === 'allowed') {
        reportFreeUsage();
      }
      await onHandleAction();
      return;
    }

    const decision = decidePlanGate({
      requiredTier: config.requiredTier,
      seatLimited: config.seatLimited,
      hasSubscription,
      hasProPlanSubscription,
      seats,
    });
    if (decision !== 'allowed') {
      openPaywall(decision, onBeforePaywall);
      return;
    }
    await onHandleAction();
  };
};
